import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNav from './PublicNav';
import PublicFooter from './PublicFooter';
import './HomeScreen.css';

const HomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <div className="home-screen">
      {/* Navigation Header */}
      <PublicNav onLoginClick={handleLogin} />

      {/* Hero Banner Section */}
      <section className="hero-banner">
        <video
          ref={videoRef}
          className="hero-video"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src="/video/intro.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="hero-title">Digitize Asset Ownership on the Blockchain</h1>
          <p className="hero-description">
            Track ownership, manage vesting, and calculate distributions for companies, properties, funds, and more.
          </p>
          <button className="login-button" onClick={handleLogin}>
            Login
          </button>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="benefits-section">
        <div className="benefits-container">
          <h2 className="benefits-title">Transparent Ownership, Simplified</h2>
          <p className="benefits-intro">Replace spreadsheets and manual tracking with blockchain-powered ownership records that update automatically.</p>
          <div className="benefit-items">
            <div className="benefit-item benefit-item-1">
              <div className="benefit-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                  <line x1="3" y1="9" x2="21" y2="9"></line>
                  <line x1="9" y1="21" x2="9" y2="9"></line>
                </svg>
              </div>
              <h3>Ownership Records</h3>
              <p>See who owns what. Always transparent, always up to date.</p>
            </div>
            <div className="benefit-item benefit-item-2">
              <div className="benefit-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <h3>Vesting Tracking</h3>
              <p>Automatically calculate when equity vests over time.</p>
            </div>
            <div className="benefit-item benefit-item-3">
              <div className="benefit-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
              </div>
              <h3>Distributions</h3>
              <p>Calculate dividends and profit shares automatically.</p>
            </div>
          </div>
        </div>
      </section>

      {/* For Asset Creators */}
      <section className="creators-section">
        <div className="creators-container">
          <h2 className="creators-title">Create Valuable Assets</h2>
          <p className="creators-subtitle">Turn your company, property, or fund into a digitized asset with transparent ownership records</p>
          <div className="creators-items">
            <div className="creator-item">
              <div className="creator-number">1</div>
              <h3>Set Up Your Asset</h3>
              <p>Create your asset on the platform and deploy it to the blockchain. Your ownership structure becomes transparent and immutable.</p>
            </div>
            <div className="creator-item">
              <div className="creator-number">2</div>
              <h3>Invite Owners</h3>
              <p>Add owners, investors, or employees. Set up vesting schedules and allocate ownership units with a few clicks.</p>
            </div>
            <div className="creator-item">
              <div className="creator-number">3</div>
              <h3>Manage Everything</h3>
              <p>Declare distributions, track vesting, and manage ownership—all automatically calculated and recorded.</p>
            </div>
          </div>
        </div>
      </section>

      {/* For Owners */}
      <section className="owners-section">
        <div className="owners-container">
          <h2 className="owners-title">See Your Ownership Grow</h2>
          <p className="owners-subtitle">Watch your equity vest, track your holdings, and see distributions—all in one clear dashboard</p>
          <div className="owners-items">
            <div className="owner-item">
              <div className="owner-number">1</div>
              <h3>Connect & View</h3>
              <p>Link your wallet and instantly see your ownership percentage and holdings across all your assets.</p>
            </div>
            <div className="owner-item">
              <div className="owner-number">2</div>
              <h3>Watch It Vest</h3>
              <p>See your equity vest in real-time. No guessing, no spreadsheets—just clear visibility into what's yours.</p>
            </div>
            <div className="owner-item">
              <div className="owner-number">3</div>
              <h3>Get Paid</h3>
              <p>See exactly what you're owed from distributions. Know your share before payments are made.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-home-section">
        <div className="how-it-works-home-container">
          <h2 className="how-it-works-home-title">How It Works</h2>
          <p className="how-it-works-home-subtitle">Four simple steps from sign-in to success</p>
          
          <div className="how-it-works-steps">
            <div className="how-step how-step-1">
              <div className="how-step-visual">
                <div className="how-step-circle">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
                <div className="how-step-arrow">→</div>
              </div>
              <div className="how-step-content">
                <div className="how-step-number">1</div>
                <h3>Sign In</h3>
                <p>Email login, no passwords. We send you a code.</p>
              </div>
            </div>

            <div className="how-step how-step-2">
              <div className="how-step-visual">
                <div className="how-step-circle">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                </div>
                <div className="how-step-arrow">→</div>
              </div>
              <div className="how-step-content">
                <div className="how-step-number">2</div>
                <h3>Create or Access</h3>
                <p>Creators set up assets. Owners view their holdings.</p>
              </div>
            </div>

            <div className="how-step how-step-3">
              <div className="how-step-visual">
                <div className="how-step-circle">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                  </svg>
                </div>
                <div className="how-step-arrow">→</div>
              </div>
              <div className="how-step-content">
                <div className="how-step-number">3</div>
                <h3>Manage Ownership</h3>
                <p>Allocate units, set vesting, connect wallets.</p>
              </div>
            </div>

            <div className="how-step how-step-4">
              <div className="how-step-visual">
                <div className="how-step-circle">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                  </svg>
                </div>
              </div>
              <div className="how-step-content">
                <div className="how-step-number">4</div>
                <h3>Track & Distribute</h3>
                <p>Everything updates automatically. You're done.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default HomeScreen;

